import { useContext, useEffect, useState } from "react";

import Typography from "@/design-system/Typography";
import ChevronBottom from "@/design-system/icons/chevron-bottom";
import PulseLoading from "@/design-system/pulse-loading";
import TelemetryButton from "@/design-system/telemetry-button";
import { FetchGet } from "@/lib/fetch-helper";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { AvailabilityState, CashbackComplaintStatus } from "@/src/cashback/CashbackCommonInterfaces";
import type { IComplaintNew } from "@/src/cashback/MainPage/ComplaintNew";
import ComplaintNew from "@/src/cashback/MainPage/ComplaintNew";
import StatusLabel from "@/src/cashback/MainPage/StatusLabel";
import TextLabel from "@/src/cashback/MainPage/TextLabel";
import { TransactionItemResources } from "@/src/cashback/MainPage/Transactions/TransactionItem.Resources";
import { TransactionItemResourcesEn } from "@/src/cashback/MainPage/Transactions/TransactionItem.Resources.En";
import type { ITransactionItem } from "@/src/cashback/MainPage/Transactions/shared";
import { TransactionsContext } from "@/src/cashback/MainPage/TransactionsContext";
import { languageSelector } from "@/src/contexts";
import { useMutationCustom } from "@/src/query-client-overrides";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QuerySmMax, colors } from "../../../../styles/tokens.stylex";

export default function TransactionItemMobile(props: ITransactionItem) {
    const [isExtended, setIsExtended] = useState(false);

    const { claim } = useContext(TransactionsContext);
    const onClaimClick = (): void => claim(props);

    const resources = languageSelector(TransactionItemResources, TransactionItemResourcesEn);

    const {
        mutate,
        data: complainList,
        isPending: isLoading,
    } = useMutationCustom({
        mutationKey: ["ComplaintList", props.id],
        mutationFn: () => FetchGet<IComplaintNew[]>(`/api/cashback/ComplaintList?id=${props.id}`),
        onSuccess: (data) => {
            if (data) {
                setIsExtended(true);
            }
        },
    });

    const renderTrxInfo = (text: string) =>
        text && (
            <Typography textElement="p" styleX={styles.transactionInfo}>
                {text}
            </Typography>
        );

    const renderWithdrawalInfo =
        props.availability !== AvailabilityState.Unknown ? null : (
            <>
                {props.accountNumber && !props.hasCreditCard && (
                    <TextLabel
                        id="transactionAccountNumber"
                        variant="blueBorder"
                        styleX={styles.withdrawalText}
                        labelText={resources.accountNumberLabel}
                        text={props.accountNumber}
                        hideIcon
                    />
                )}
                {props.recipient && (
                    <TextLabel
                        id="transactionRecipient"
                        variant="blueBorder"
                        styleX={[styles.withdrawalText, styles.withdrawalSpace]}
                        labelText={resources.recipientLabel}
                        text={props.recipient}
                        hideIcon
                    />
                )}
            </>
        );

    const renderOrderInfo = (
        <div {...stylex.props(styles.gridOrderInfo)}>
            <Typography textElement="p" fontStyle="BodyM" styleX={styles.boldText}>
                {props.brandName}
            </Typography>

            <Typography textElement="p" fontStyle="BodyS" styleX={styles.smallText}>
                {props.date}
            </Typography>
            {props.complaintStatus !== CashbackComplaintStatus.Unknown && (
                <div {...stylex.props(styles.complaintStatus)}>
                    <Typography textElement="p" fontStyle="BodyS" styleX={styles.complaintStatusLabel}>
                        {resources.complaintStatusLabel}
                    </Typography>
                    <StatusLabel
                        status={props.complaintStatus}
                        statusLabel={props.complaintStatusDescription}
                    />
                </div>
            )}
        </div>
    );

    const renderOrderInfoAndPurchaseAmount = (!!props.orderId ||
        !!props.conditionsToApproveDescription ||
        !!props.purchaseAmount) && (
        <div {...stylex.props(styles.orderInfoAndPurchaseAmount)}>
            {props.orderId && (
                <div>
                    {props.purchaseAmount && (
                        <Typography textElement="span" fontStyle="BodyS" styleX={styles.label}>
                            {resources.orderIdMobileLabel}
                        </Typography>
                    )}
                    <Typography textElement="span" fontStyle="BodyM" styleX={styles.text}>
                        {props.orderId}
                    </Typography>
                </div>
            )}

            {props.conditionsToApproveDescription && (
                <div>
                    <Typography textElement="span" fontStyle="BodyS" styleX={styles.label}>
                        {resources.trxConditionsLabel}
                    </Typography>
                    <Typography textElement="span" fontStyle="BodyM" styleX={styles.text}>
                        {props.conditionsToApproveDescription}
                    </Typography>
                </div>
            )}

            {props.purchaseAmount && (
                <div>
                    <Typography textElement="span" fontStyle="BodyS" styleX={styles.label}>
                        {resources.purchaseAmountLabel}
                    </Typography>
                    <Typography textElement="span" fontStyle="BodyM" styleX={styles.text}>
                        {props.purchaseAmount}
                    </Typography>
                    {props.deliveryCostInfo && (
                        <Typography textElement="span" fontStyle="BodyS" styleX={styles.smallLabel}>
                            {props.deliveryCostInfo}
                        </Typography>
                    )}
                </div>
            )}
        </div>
    );

    const onTransactionItemClick = () => {
        const isWithdrawal = props.availability === AvailabilityState.Unknown;

        if (!isExtended && !complainList && !isWithdrawal) {
            mutate();
        }

        sendTelemetryEvent({
            eventName: `cshbck-${isWithdrawal ? "wthdrDetailsOpen" : "trxDetailsOpen"}`,
            actionType: "Click",
            itemId: props.id,
        });

        setIsExtended(!isExtended);
    };

    useEffect(() => {
        if (props.isExtended) {
            mutate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div {...stylex.props(styles.main, isExtended && styles.backgroundChange)}>
            <div {...stylex.props(styles.item)} onClick={onTransactionItemClick}>
                {renderOrderInfo}
                <div {...stylex.props(styles.gridAmountCb)}>
                    <Typography textElement="span" fontStyle="BodyM" styleX={styles.amountCB}>
                        {props.returnedAmount}
                    </Typography>
                    <span {...stylex.props(styles.arrow, isExtended && styles.arrowRotate)}>
                        <ChevronBottom />
                    </span>
                </div>
            </div>

            <div {...stylex.props(styles.extraInfo, isExtended && styles.extraInfoOpen)}>
                <div {...stylex.props(styles.innerExtraInfo)}>
                    {renderOrderInfoAndPurchaseAmount}
                    {props.purchaseAmount && renderTrxInfo(props.transactionInfo)}
                    {props.availability === AvailabilityState.Rejected &&
                        renderTrxInfo(props.rejectionReasonTypeDescription)}
                    {isLoading ? (
                        <PulseLoading
                            pulseNumber={2}
                            styleX={styles.loading}
                            stylexItem={styles.loadingItem}
                        />
                    ) : (
                        complainList &&
                        complainList.map((val, index) => (
                            <ComplaintNew {...val} key={index} styleX={styles.complaintNewContainer} />
                        ))
                    )}
                    {props.canCreateComplaint && (
                        <TelemetryButton
                            as="button"
                            variant="magentaRound"
                            size="M"
                            onClick={onClaimClick}
                            styleX={styles.createComplaintBtn}
                            eventName="cshbck-CashbackBrand"
                            itemId={props.brandId}
                        >
                            {complainList?.length > 0 ? resources.btnTextAgain : resources.btnText}
                        </TelemetryButton>
                    )}
                    {renderWithdrawalInfo}
                </div>
            </div>
        </div>
    );
}

const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    main: {
        borderBottomColor: colors.b_Primary,
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
    },

    item: {
        padding: { default: ComposableSpacing.s3, [smMax]: ComposableSpacing.s1 },
        gap: ComposableSpacing.s3,
        alignItems: "center",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gridTemplateRows: "1fr",
        minHeight: 90,
    },

    backgroundChange: {
        backgroundColor: "#fff",
        transitionDuration: "300ms",
        transitionProperty: "background-color",
        transitionTimingFunction: "ease-out",
    },

    gridOrderInfo: {
        gridColumn: "1/2",
        alignItems: "flex-start",
        display: "inline-flex",
        flexDirection: "column",
        justifyContent: "center",
    },

    gridAmountCb: {
        gridColumn: "2/3",
        justifySelf: "end",
    },

    boldText: {
        fontWeight: "bold",

        paddingBottom: ComposableSpacing.s1,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: ComposableSpacing.s1,
    },

    smallText: {
        color: colors.grayInactive,
    },

    complaintStatus: {
        gap: ComposableSpacing.s1,
        alignItems: "center",
        display: "inline-flex",
        paddingTop: ComposableSpacing.s1,
    },

    complaintStatusLabel: {
        fontSize: ".75rem",
    },

    complaintNewContainer: {
        borderBottomColor: colors.fg_Tertiary,
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
    },

    createComplaintBtn: {
        margin: 10,
        fontSize: ".75rem",
        justifySelf: "end",
    },

    transactionInfo: {
        fontSize: ".75rem",
        fontWeight: "bold",
    },

    extraInfo: {
        overflow: "hidden",
        display: "grid",
        gridTemplateRows: "0fr",

        transitionDuration: "300ms",
        transitionProperty: "grid-template-rows",
        transitionTimingFunction: "ease-out",
        paddingBottom: 0,
        paddingLeft: { default: 70, [smMax]: ComposableSpacing.s2 },
        paddingRight: { default: ComposableSpacing.s2, [smMax]: ComposableSpacing.s1 },
        paddingTop: 0,
    },

    extraInfoOpen: {
        gridTemplateRows: "1fr",
    },

    innerExtraInfo: {
        gap: ComposableSpacing.s2,
        overflow: "hidden",
        display: "grid",
    },

    orderInfoAndPurchaseAmount: {
        color: "#242424",
        paddingBottom: "0.875rem",
    },

    label: {
        color: colors.fg_Tertiary,
        fontWeight: 400,

        textTransform: "uppercase",
        marginBottom: 0,
    },

    text: {
        display: "block",
        fontSize: ".875rem",
        fontWeight: "bold",
        wordBreak: "break-word",
    },

    smallLabel: {
        margin: { default: null, [smMax]: "-3px 0 0 0" },
        color: colors.fg_Tertiary,
        display: { default: "unset", [smMax]: "block" },
        fontSize: { default: "0.8125rem", [smMax]: "0.75rem" },
    },

    amountCB: {
        fontWeight: "bold",
        paddingRight: ComposableSpacing.s2,
    },

    arrow: {
        strokeWidth: 4,
        color: colors.b_BrandMagenta,
        display: "inline-block",
        flexShrink: 0,
        transitionDuration: "180ms",
        transitionProperty: "transform",
        transitionTimingFunction: "ease-out",
        verticalAlign: "sub",
        height: 20,
        width: 20,
    },

    arrowRotate: {
        transform: "rotate(180deg)",
    },

    loading: {
        gap: ComposableSpacing.s3,
    },

    loadingItem: {
        borderRadius: ComposableSpacing.s1,
        height: ComposableSpacing.s4,
        width: { default: "40%", [":first-child"]: "25%" },
    },

    withdrawalText: {
        fontSize: ".75rem",
    },
    withdrawalSpace: {
        paddingBottom: ComposableSpacing.s3,
    },
});
